import { computed, ref, readonly } from 'vue'
import { clone } from 'ramda'

import { useCore } from '@/composables/core'
import { useModal } from '@/composables/modal'
import { ENDPOINTS } from '@/lib/consts.ts'
import AgreementText from '@/components/shared/agreement-text.vue'

export const SMS_MARKETING_CONSENT = 'receiving_messages_consent'

const cachedAgreements = ref(null)

export function useGdprAgreements () {
  const { apiCall, store, t } = useCore()
  const modal = useModal()

  const business = computed(() => store.state.business?.business)

  const isUSMarketingConsentEnabled = computed(() =>
    store.state.routingConfig.config.country?.code === 'us'
  )

  const shouldIncludeAgreementInPut = (agreement) =>
    !(
      isUSMarketingConsentEnabled.value
      && agreement.name === SMS_MARKETING_CONSENT
      && agreement.value == null
    )

  const saveAgreements = (agreements) => {
    const filteredAgreements = agreements.filter(
      (a) =>
        !a.information
        && shouldIncludeAgreementInPut(a)
    )
    return apiCall({
      method: 'PUT',
      url: ENDPOINTS.BUSINESS_AGREEMENTS,
      urlParams: {
        businessId: business.value?.id,
      },
      data: {
        business_agreements: filteredAgreements,
      },
    })
  }

  const loadAgreements = async () => {
    if (cachedAgreements.value) return cachedAgreements.value

    const data = await apiCall({
      method: 'GET',
      url: ENDPOINTS.AGREEMENTS,
    }) as { data?: { business_agreements?: unknown } }

    return data?.data?.business_agreements
  }

  const isGdprEnabled = computed(() =>
    store.state.routingConfig.config.gdpr
    && (!store.state.business?.business || store.state.business.business.is_gdpr_first_run)
  )

  const cacheAgreements = (agreements) => {
    cachedAgreements.value = clone(agreements)
  }

  const clearCachedAgreements = () => {
    cachedAgreements.value = null
  }

  const isUSMarketingConsent = (agreement) =>
    isUSMarketingConsentEnabled.value && agreement?.name === SMS_MARKETING_CONSENT

  const shouldShowConsentModal = (agreement, currentValue = agreement?.value) =>
    isUSMarketingConsent(agreement) && !currentValue

  const openUSMarketingConsentModal = ({ agreement, onConfirm }) => {
    modal.open({
      header: {
        $icon: 'x',
        $title: t('settings.agreements.dialog.confirm.title'),
      },
      content: {
        $component: {
          is: AgreementText,
          class: 'text-left',
          description: agreement.description,
          '@linkClicked': ({ event, href }) => {
            event.preventDefault()
            window.open(href, '_blank')
          },
        },
      },
      buttons: {
        $text: t('confirm'),
        size: 'lg',
        theme: 'primary',
        '@click': () => {
          onConfirm()
          modal.close()
        },
      },
    })
  }

  return {
    cacheAgreements,
    cachedAgreements: readonly(cachedAgreements),
    clearCachedAgreements,
    saveAgreements,
    loadAgreements,
    isGdprEnabled,
    isUSMarketingConsentEnabled,
    isUSMarketingConsent,
    shouldShowConsentModal,
    openUSMarketingConsentModal,
    shouldIncludeAgreementInPut,
  }
}
